import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { GuestLayout } from 'components/templates';
import useAuthentication from 'base/auth/hooks/use-authentication';
import ResetPasswordFormValidation from 'pages/reset-password/validation/reset-password-form-validation';
import logo from 'assets/images/slocoach-logo.svg';
import { selectAccessToken, selectLoader } from 'base/auth/selectors';
import { authSliceActions } from 'base/auth/slice';
import { useConfirmation } from 'base/common';
import ResetPasswordFormView from '../reset-password-form';

const ResetPasswordView = () => {
  useAuthentication();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get('email');
  const verificationCode = urlParams.get('verificationCode');
  const confirm = useConfirmation();

  const methods = useForm({
    defaultValues: {
      verificationCode,
      name: email,
      newPassword: '',
      confirmPassword: '',
      retainLogin: false,
    },
    resolver: yupResolver(ResetPasswordFormValidation),
    shouldFocusError: true,
  });
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const token = selectAccessToken(); // Direct call to the function instead of using useSelector

  useEffect(() => {
    if (token) navigate('/');
  }, [token, navigate]);

  const handleSubmitFrom: SubmitHandler<any> = useCallback(
    async (data) => {
      await new Promise((resolve, reject) => {
        dispatch(authSliceActions.resetPassword({ ...data, resolve, reject }));
      });
      confirm({
        variant: 'info',
        icon: 'success',
        message: 'Your account password reset has been success. Please login with new password.',
      }).then(() => {
        window.location.href = '/login';
      });
    },
    [dispatch],
  );

  const formWrapperStyles = { width: { xs: '100%', md: '600px' } };

  return (
    <GuestLayout loading={loading}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        className="text-center py-4"
      >
        <Grid item sx={formWrapperStyles}>
          <Typography variant="h2" sx={{ mb: 6 }}>Reset your password</Typography>
          <FormProvider {...methods}>
            <ResetPasswordFormView onSubmit={handleSubmitFrom} />
          </FormProvider>
          <div className="text-center mx-0 mx-lg-4">
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}> Enter a new password and then repeat it. </Typography>
            <img src={logo} alt="Slo-Coach" />
          </div>
        </Grid>
      </Grid>
    </GuestLayout>
  );
};

export default ResetPasswordView;
